import type { DocumentationConfigs } from '@si/types';
import { patch } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  reportId: string | number;
  configs: DocumentationConfigs;
  filename: string;
}

export function useUpdateReportDraft() {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, error, isPending, isSuccess, reset } = useMutation<
    void,
    Error,
    Params
  >({
    mutationFn: ({ projectId, reportId, configs, filename }) =>
      patch({
        url: `/projects/${projectId}/reports/${reportId}/draft`,
        body: { configs, filename },
      }).then(() => undefined),
    onSuccess: (_, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.getReports(projectId) }),
  });

  const updateReportDraft = useCallback((params: Params) => mutate(params), [mutate]);
  const updateReportDraftAsync = useCallback((params: Params) => mutateAsync(params), [mutateAsync]);

  return {
    updateReportDraft,
    updateReportDraftAsync,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
